/**
 * Context menus: a MenuPopup opened at the pointer (or under a focused row for
 * the keyboard's context-menu key and Shift+F10), kept on screen, closed by
 * Escape, an outside click or a chosen item, with focus returned to where the
 * menu was opened from.
 *
 * Each surface builds its own items from commands it already dispatches, so
 * this component knows nothing about entities or levels.
 */
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { contextMenuPosition } from "./contextMenuPosition";
import { MenuPopup } from "./Menu";
import type { MenuItem } from "./Menu";
import { useDismissOnOutsideClick } from "./useDismissOnOutsideClick";

interface OpenMenu {
  readonly label: string;
  readonly items: readonly MenuItem[];
  readonly at: { readonly x: number; readonly y: number };
  /** Where focus goes back to when the menu closes. */
  readonly returnFocus: HTMLElement | null;
}

export interface ContextMenuHandle {
  /** Opens a menu at a page point (clientX/clientY). */
  readonly open: (at: { x: number; y: number }, label: string, items: readonly MenuItem[]) => void;
  /** The menu element to render in the surface (null when closed). */
  readonly element: ReactNode;
}

/** True for the keyboard's ways of asking for a context menu. */
export function isContextMenuKey(event: { key: string; shiftKey: boolean }): boolean {
  return event.key === "ContextMenu" || (event.shiftKey && event.key === "F10");
}

/** Where a keyboard-opened menu appears: under the focused element's start, like Windows. */
export function menuPointFor(element: Element): { x: number; y: number } {
  const rect = element.getBoundingClientRect();
  return { x: rect.left + 24, y: rect.bottom };
}

export function useContextMenu(): ContextMenuHandle {
  const [menu, setMenu] = useState<OpenMenu | null>(null);

  const close = () => {
    menu?.returnFocus?.focus();
    setMenu(null);
  };

  // Stable, so surfaces can hand it to long-lived objects (the viewport's navigator).
  const open = useCallback<ContextMenuHandle["open"]>((at, label, items) => {
    const active = document.activeElement;
    setMenu({ label, items, at, returnFocus: active instanceof HTMLElement ? active : null });
  }, []);

  return { open, element: menu && <ContextMenu menu={menu} onClose={close} /> };
}

function ContextMenu({ menu, onClose }: { menu: OpenMenu; onClose: () => void }) {
  const root = useRef<HTMLDivElement>(null);
  // Measured after mount, then placed so it stays inside the window.
  const [position, setPosition] = useState({ left: menu.at.x, top: menu.at.y });

  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;
    const size = { width: element.offsetWidth, height: element.offsetHeight };
    setPosition(
      contextMenuPosition(menu.at, size, { width: window.innerWidth, height: window.innerHeight }),
    );
  }, [menu.at]);

  useDismissOnOutsideClick(root, true, onClose);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape" && event.key !== "Tab") return;
    // Escape closes this menu, not a dialog or tour behind it.
    event.preventDefault();
    event.stopPropagation();
    onClose();
  };

  return (
    <div
      ref={root}
      className="kn-context-menu"
      style={{ left: position.left, top: position.top }}
      onKeyDown={onKeyDown}
      onContextMenu={(event) => {
        event.preventDefault();
      }}
    >
      <MenuPopup label={menu.label} items={menu.items} onClose={onClose} />
    </div>
  );
}
